import { useGetActivityHistory } from '@practium/features/api';
import { fuzzyFilter } from '@practium/shared';
import type { FilterGroup } from '@practium/ui';
import { useEffect, useMemo, useState } from 'react';
import { buildUnionDateRange, isInDateRange } from '../../recommendations';

const PAGE_SIZE = 5;

const FILTER_GROUPS: FilterGroup[] = [
	{
		label: 'Período',
		options: [
			{ value: 'periodo:15', label: 'Últimos 15 dias' },
			{ value: 'periodo:30', label: 'Últimos 30 dias' },
			{ value: 'periodo:custom', label: 'Personalizado' },
		],
	},
];

export const useHistoryFilter = () => {
	const [searchQuery, setSearchQuery] = useState('');
	const [filterValues, setFilterValues] = useState<string[]>([]);
	const [customDateFrom, setCustomDateFrom] = useState('');
	const [customDateTo, setCustomDateTo] = useState('');
	const [page, setPage] = useState(1);
	const { data: items, isLoading, isError, refetch } = useGetActivityHistory({
		page,
		limit: PAGE_SIZE,
	});
	const totalPages = Math.max(1, items?.totalPages ?? 1);

	useEffect(() => {
		setPage(1);
	}, [searchQuery, filterValues, customDateFrom, customDateTo]);

	useEffect(() => {
		if (items && page > totalPages) setPage(totalPages);
	}, [items, page, totalPages]);

	const filterGroups = useMemo<FilterGroup[]>(() => {
		const categories = [
			...new Set([
				...(items?.docs ?? []).map((item) => item.category).filter(Boolean),
				...filterValues
					.filter((value) => value.startsWith('category:'))
					.map((value) => value.slice('category:'.length)),
			]),
		].sort((a, b) => a.localeCompare(b, 'pt-BR'));

		return [
			{
				label: 'Categoria',
				options: categories.map((category) => ({
					value: `category:${category}`,
					label: category,
				})),
			},
			...FILTER_GROUPS,
		];
	}, [items?.docs, filterValues]);

	const periodoFilters = useMemo(
		() => filterValues.filter((v) => v.startsWith('periodo:')).map((v) => v.slice(8)),
		[filterValues]
	);

	const dateRange = useMemo(
		() => buildUnionDateRange(periodoFilters, customDateFrom, customDateTo),
		[periodoFilters, customDateFrom, customDateTo]
	);

	const filteredItems = useMemo(() => {
		let result = fuzzyFilter(items?.docs ?? [], searchQuery, (item) =>
			[item.category, item.action, item.description].filter(Boolean).join(' ')
		);

		const categoryFilters = filterValues
			.filter((value) => value.startsWith('category:'))
			.map((value) => value.slice('category:'.length));

		if (categoryFilters.length > 0) {
			result = result.filter((item) => categoryFilters.includes(item.category));
		}

		if (periodoFilters.length > 0) {
			result = result.filter((item) => isInDateRange(item.createdAt, dateRange.from, dateRange.to));
		}

		return result;
	}, [items, searchQuery, filterValues, dateRange, periodoFilters]);

	const hasCustomPeriod = periodoFilters.includes('custom');

	return {
		page,
		setPage,
		totalPages,
		isLoading,
		isError,
		refetch,
		searchQuery,
		setSearchQuery,
		filterValues,
		setFilterValues,
		filterGroups,
		customDateFrom,
		setCustomDateFrom,
		customDateTo,
		setCustomDateTo,
		hasCustomPeriod,
		filteredItems,
	};
};
